import {
  CreateUserLanguageLearningObjectives,
  UpdateUserDemographics,
} from "@/client/core";
import { DEMOGRAPHICS, LANGUAGES_OBJECTIVES, StepsFlags } from "@/lib/userInfo";
import DemographicsForm, {
  DemographicFormSchema,
  DemographicFormValues,
} from "@/components/pages/user-info/demographics-form-controlled";
import { FormProvider, useForm } from "react-hook-form";
import LanguageLearningObjectivesForm, {
  LanguageLearningObjectivesFormSchema,
  LanguageLearningObjectivesFormValues,
} from "@/components/pages/user-info/language-objectives-form-controlled";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { CardWrapper } from "@/components/card-wrapper";
import { Check } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { TopNavComponent } from "@/components/navigation/top-nav";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { usePocketBase } from "@/context/pocketbase-context";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";

type Step = {
  num: number;
  id: string;
  title: string;
  description: string;
};

export default function UserInfoForm() {
  const { t } = useTranslation(["pages", "common"]);
  const { pb, token, user } = usePocketBase();

  const [stepsBuilt, setStepsBuilt] = useState(false);
  const [STEPS, setSteps] = useState<Step[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [demographicsDefaults, setDemographicsDefaults] = useState<
    DemographicFormValues | undefined
  >(undefined);

  const navigate = useNavigate();

  useEffect(() => {
    console.log("Evaluating steps for user:", user);
    console.log(user?.flags);

    if (stepsBuilt) {
      console.log("Steps already built, skipping...");
      return;
    } else {
      console.log("Building steps...");
    }

    if (!user) {
      return;
    }
    const stepsToBuild: Step[] = [];
    const userFlags = user.flags?.map((flag) => flag.key) || [];
    if (!userFlags.includes(StepsFlags.get(DEMOGRAPHICS) as string)) {
      stepsToBuild.push({
        num: stepsToBuild.length + 1,
        id: DEMOGRAPHICS,
        title: "pages:userInfoProgressive.steps.demographics.title",
        description: "pages:userInfoProgressive.steps.demographics.description",
      });
    }

    if (!userFlags.includes(StepsFlags.get(LANGUAGES_OBJECTIVES) as string)) {
      stepsToBuild.push({
        num: stepsToBuild.length + 1,
        id: LANGUAGES_OBJECTIVES,
        title:
          "pages:userInfoProgressive.steps.learningLanguageObjectives.title",
        description:
          "pages:userInfoProgressive.steps.learningLanguageObjectives.description",
      });
    }

    setSteps(stepsToBuild);
    setStepsBuilt(true);

    console.log("STEPS to complete:", stepsToBuild);

    if (stepsToBuild.length === 0) {
      // No pending steps to complete, navigate to main app
      console.log("No pending steps, navigating to main app");
      navigate("/app/voicechat");
      return;
    }
  }, [user, stepsBuilt]);

  const demographicsForm = useForm<DemographicFormValues>({
    resolver: zodResolver(DemographicFormSchema),
    defaultValues: demographicsDefaults ?? {
      firstName: "",
      lastName: "",
      gender: "",
      ageRange: "",
      country: "",
      nativeLanguage: "",
    },
  });

  const onSubmitDemographics = async (values: DemographicFormValues) => {
    // TODO: Something like this to prevent resubmission, likely using useCallback
    // if (completedSteps.includes(1) && values === demographicsDefaults) {
    //   return; // Prevent resubmission if already completed
    // }

    setIsSubmitting(true);
    setDemographicsDefaults(values);

    const response = await UpdateUserDemographics(values, user?.id, token);
    if (response !== "success") {
      toast.error(`Failed to update demographics: ${response}`);
      setIsSubmitting(false);
      return;
    }

    const stepNumber = STEPS.find((step) => step.id === DEMOGRAPHICS)?.num;
    if (stepNumber) {
      setCompletedSteps((prev) => [...prev, stepNumber]);
    }
    setCurrentStep((prev) => prev + 1);
    setIsSubmitting(false);
  };

  const [languageLearningDefaults, setLanguageLearningDefaults] = useState<
    LanguageLearningObjectivesFormValues | undefined
  >(undefined);

  const languageLearningForm = useForm<LanguageLearningObjectivesFormValues>({
    resolver: zodResolver(LanguageLearningObjectivesFormSchema),
    mode: "onChange", // Validate on change
    defaultValues: languageLearningDefaults ?? {
      learnLanguages: [
        {
          id: "new_1",
          languageCode: "",
          proficiency: "A1",
          learningObjectives: [],
        },
      ],
    },
  });

  const onSubmitLanguageLearningObjectives = async (
    values: LanguageLearningObjectivesFormValues,
  ) => {
    // TODO: Something like this to prevent resubmission, likely using useCallback
    // if (completedSteps.includes(2) && values === languageLearningDefaults) {
    //   return; // Prevent resubmission if already completed
    // }

    setIsSubmitting(true);
    setLanguageLearningDefaults(values);
    // Assuming you have a function to update language learning objectives
    const response = await CreateUserLanguageLearningObjectives(
      values,
      user?.id,
      token,
    );
    if (response !== "success") {
      toast.error(`Failed to update learning objectives: ${response}`);
    }

    console.log("Language learning objectives updated successfully");

    const stepNumber = STEPS.find(
      (step) => step.id === LANGUAGES_OBJECTIVES,
    )?.num;
    if (stepNumber) {
      setCompletedSteps((prev) => [...prev, stepNumber]);
    }
    setCurrentStep((prev) => prev + 1);
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (STEPS && STEPS.length > 0 && currentStep > STEPS.length) {
      async function refreshUserData() {
        await pb.collection("users").authRefresh();
      }
      // Refresh user data to get updated flags after completing all steps
      void refreshUserData();
      // All steps completed, navigate to main app
      navigate("/app/voicechat");
    }
  }, [currentStep, STEPS]);

  function NavigationButtons({ formName }: { formName: string }) {
    return (
      // Navigation Buttons
      <div className="flex justify-between mt-6 pt-6 border-t">
        {STEPS.length > 1 ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => setCurrentStep((prev) => Math.max(1, prev - 1))}
            disabled={currentStep === 1}
            className="min-w-28 md:min-w-40"
          >
            {t("common:buttons.previous")}
          </Button>
        ) : (
          <div />
        )}
        <Button
          type="submit"
          disabled={isSubmitting}
          form={formName}
          className="min-w-28 md:min-w-40"
        >
          {t("common:buttons.next")}
        </Button>
      </div>
    );
  }

  function renderFormStep() {
    const step = STEPS.find((step) => step.num === currentStep);
    if (!step) return null;

    switch (step.id) {
      case DEMOGRAPHICS:
        return (
          <FormProvider {...demographicsForm}>
            <form
              id="demographicsForm"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={demographicsForm.handleSubmit(onSubmitDemographics)}
              className="space-y-4"
            >
              <DemographicsForm />
              <NavigationButtons formName="demographicsForm" />
            </form>
          </FormProvider>
        );
      case LANGUAGES_OBJECTIVES:
        return (
          <FormProvider {...languageLearningForm}>
            <form
              id="languageLearningForm"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={languageLearningForm.handleSubmit(
                onSubmitLanguageLearningObjectives,
              )}
              className="space-y-4"
            >
              <LanguageLearningObjectivesForm />
            </form>
            <NavigationButtons formName="languageLearningForm" />
          </FormProvider>
        );
      default:
        return <Spinner className="size-4" />;
    }
  }

  return (
    <>
      <div className="pt-12 flex justify-center items-center w-screen h-screen p-4">
        <CardWrapper className="max-w-lg md:max-w-2xl w-full px-6 md:px-10">
          <div className="mb-4 text-center">
            <p className="text-base sm:text-lg text-gray-600">
              {t("pages:userInfoProgressive.subheader")}
            </p>
          </div>

          {/* Step Progress Navigation */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {/* Show step numbers inf there are 2 or more pending steps */}
              {STEPS.length > 1 &&
                STEPS.map((step, index) => (
                  <>
                    <div
                      key={step.num}
                      className="flex items-center flex-1 justify-center"
                    >
                      <button
                        className={cn(
                          "flex flex-col items-center gap-2 transition-all group",
                          step.num <= currentStep
                            ? "cursor-pointer"
                            : "cursor-not-allowed opacity-50",
                        )}
                        disabled={
                          step.num > currentStep &&
                          !completedSteps.includes(step.num - 1)
                        }
                      >
                        <div
                          className={cn(
                            "w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 transition-all font-semibold",
                            completedSteps.includes(step.num)
                              ? "bg-primary border-primary text-primary-foreground"
                              : step.num === currentStep
                                ? "bg-accent border-primary text-foreground scale-110"
                                : "bg-background border-border text-muted-foreground",
                          )}
                        >
                          {completedSteps.includes(step.num) ? (
                            <Check className="w-5 h-5" />
                          ) : (
                            step.num
                          )}
                        </div>
                        <div className="text-center hidden sm:block">
                          <div
                            className={cn(
                              "text-sm font-medium",
                              step.num === currentStep
                                ? "text-foreground"
                                : completedSteps.includes(step.num)
                                  ? "text-foreground"
                                  : "text-muted-foreground",
                            )}
                          >
                            {t(step.title)}
                          </div>
                          <div className="text-xs text-muted-foreground hidden md:block">
                            {t(step.description)}
                          </div>
                        </div>
                      </button>
                    </div>
                    {index < STEPS.length - 1 ? (
                      <div
                        className={cn(
                          "h-[2px] flex-1 mx-2 transition-all flex-stretch",
                          completedSteps.includes(step.num)
                            ? "bg-primary"
                            : "bg-border",
                        )}
                      />
                    ) : null}
                  </>
                ))}
            </div>
          </div>

          <div>{renderFormStep()}</div>
        </CardWrapper>
      </div>
      <TopNavComponent hasLanguageSelector={false} />
    </>
  );
}
